import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  MonthlyFinanceAudit,
  MonthlyFinanceAuditsParams,
  MonthlyFinanceOverrideEntry,
  MonthlyFinanceProfitAndLoss,
  MonthlyFinanceProfitAndLossParams,
} from 'src/models/monthly-finance.model';

const MAX_ENTRIES_PER_PUT = 120;

@Injectable({ providedIn: 'root' })
export class MonthlyFinanceApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api_url;

  getProfitAndLoss(params: MonthlyFinanceProfitAndLossParams): Observable<MonthlyFinanceProfitAndLoss> {
    const url = `${this.baseUrl}api/v1/customer/monthly-finances/profit-and-loss`;
    return this.http.get<MonthlyFinanceProfitAndLoss>(url, { params: toHttpParams(params) });
  }

  upsertOverrides(entries: MonthlyFinanceOverrideEntry[]): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/monthly-finances`;
    if (entries.length <= MAX_ENTRIES_PER_PUT) {
      return this.http.put<void>(url, { entries });
    }
    const chunks: MonthlyFinanceOverrideEntry[][] = [];
    for (let i = 0; i < entries.length; i += MAX_ENTRIES_PER_PUT) {
      chunks.push(entries.slice(i, i + MAX_ENTRIES_PER_PUT));
    }
    return forkJoin(chunks.map(chunk => this.http.put<void>(url, { entries: chunk }))).pipe(
      map(() => undefined),
    );
  }

  listAudits(params: MonthlyFinanceAuditsParams = {}): Observable<MonthlyFinanceAudit[]> {
    const url = `${this.baseUrl}api/v1/customer/monthly-finances/audits`;
    return this.http.get<MonthlyFinanceAudit[]>(url, { params: toHttpParams(params) });
  }

}

function toHttpParams<T extends object>(input: T): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) { continue; }
    params = params.set(key, String(value));
  }
  return params;
}
